import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, EllipsisVertical, Folder, FolderOpen, Library, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useQueries, type UseQueryResult } from "@tanstack/react-query";

import { knowledgeKeys, listKbNodes, useKbAgents, useKnowledgeBases } from "@/shared/api/knowledge";
import { useAgents } from "@/shared/api/agents";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { IconButton } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { DeleteKbDialog, KbFormDialog } from "./KbDialogs";
import { KbAddMenu, menuCls, type AddKind } from "./KbAddMenu";
import { compact, formatSize } from "./format";
import { useFileDrop } from "./hooks/useFileDrop";
import { buildTree, type KbTree } from "./hooks/useTree";

import type { KbNode, KbNodeKind, KbUpload, KnowledgeBase } from "@/shared/types";

/* The rail beside a base: New, then every base of the workspace as a tree of its folders
   (files are the table's). A base's ⋮ edits or deletes it. Arrow keys walk the tree
   (WAI-ARIA tree pattern): ↑ ↓ move, → opens or steps in, ← closes or steps out, Enter
   opens. Every row takes dropped files and folders. Later, a source connected from
   outside (Google Drive …) will sit here as a folder with its source's icon. */

type Item = { key: string; base: KnowledgeBase; node: KbNode | null; level: number; parent: string | null; branches: boolean };
type BaseDialog = { kind: "create" } | { kind: "edit"; base: KnowledgeBase } | { kind: "delete"; base: KnowledgeBase };

const FOLDER: KbNodeKind = "folder";
const foldersIn = (tree: KbTree, parentId: string | null) => tree.childrenOf(parentId).filter((n) => n.kind === FOLDER);
// module-level, so TanStack runs it again only when a result changes
const toTrees = (results: UseQueryResult<KbNode[]>[]) => results.map((r) => (r.data ? buildTree(r.data) : null));

function BaseMenu({ base, onEdit, onDelete }: { base: KnowledgeBase; onEdit: () => void; onDelete: () => void }) {
  const later = (fn: () => void) => () => setTimeout(fn, 0);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label={`Actions for ${base.name}`}
          title="Manage this knowledge base"
          className="press absolute right-1 top-[3px] w-6 h-6 rounded-[6px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-3)] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 data-[state=open]:bg-[var(--surface-3)] [@media(hover:none)]:opacity-100"
        >
          <EllipsisVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" className={`w-44 ${menuCls}`}>
        <DropdownMenuItem className="gap-2 px-2 py-[5px] rounded-[8px] text-[13px] cursor-pointer focus:bg-[var(--accent-hover)]" onSelect={later(onEdit)}>
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} /> Edit details
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2 px-2 py-[5px] rounded-[8px] text-[13px] cursor-pointer text-[var(--danger)] focus:bg-[var(--accent-hover)] focus:text-[var(--danger)]" onSelect={later(onDelete)}>
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RailRow({ item, open, busy, selected, focused, count, onSelect, onToggle, onFocus, onDrop, rowRef }: {
  item: Item;
  open: boolean;
  /** Files are on their way into it. */
  busy: boolean;
  selected: boolean;
  focused: boolean;
  count?: number;
  onSelect: () => void;
  onToggle: () => void;
  onFocus: () => void;
  onDrop: (uploads: KbUpload[]) => void;
  rowRef: (el: HTMLDivElement | null) => void;
}) {
  const { node, base, level } = item;
  const drop = useFileDrop(onDrop);
  const name = node ? node.name : base.name;
  const Icon = open ? FolderOpen : Folder;

  return (
    <div
      ref={rowRef}
      role="treeitem"
      aria-level={level}
      aria-selected={selected}
      aria-expanded={item.branches ? open : undefined}
      tabIndex={focused ? 0 : -1}
      onClick={onSelect}
      onFocus={onFocus}
      title={name}
      {...drop.props}
      style={{ paddingLeft: 4 + (level - 1) * 16 }}
      className={`press h-[30px] flex items-center gap-1.5 rounded-[8px] cursor-pointer outline-none text-[13.5px] focus-visible:ring-2 focus-visible:ring-[var(--hairline-focus)] ${
        node ? "pr-2" : "pr-8" /* room for the base's ⋮ */
      } ${
        drop.over ? "bg-[var(--info-bg)] ring-1 ring-[var(--info)]"
          : selected ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
          : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
      }`}
    >
      <span
        aria-hidden="true"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={`w-4 h-4 shrink-0 flex items-center justify-center rounded-[4px] text-[var(--text-quaternary)] hover:text-[var(--text-primary)] ${item.branches ? "" : "invisible"}`}
      >
        <ChevronRight className="w-3 h-3" strokeWidth={2} style={{ transform: `rotate(${open ? 90 : 0}deg)`, transition: "transform .18s var(--easing)" }} />
      </span>
      {busy ? (
        <Loader2 aria-label="Uploading" className="w-4 h-4 shrink-0 animate-spin" strokeWidth={1.75} />
      ) : node ? (
        <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
      ) : base.icon ? (
        <span className="w-4 shrink-0 text-center text-[13px] leading-none">{base.icon}</span>
      ) : (
        <Library className="w-4 h-4 shrink-0" strokeWidth={1.75} />
      )}
      <span className="flex-1 min-w-0 truncate">{name}</span>
      {count !== undefined && (
        <span className="shrink-0 text-[11px] font-mono tabular-nums text-[var(--text-quaternary)] font-normal">{count}</span>
      )}
    </div>
  );
}

/** Who reads the open base, with their avatars. */
function UsedBy({ kbId }: { kbId: string }) {
  const { data: links } = useKbAgents(kbId);
  const { data: agents } = useAgents();
  const users = (links ?? []).map((l) => agents?.find((a) => a.id === l.agent_id)).filter((a): a is NonNullable<typeof a> => !!a);
  if (!users.length) return <p className="m-0 text-[12px] leading-[1.5] text-[var(--text-tertiary)]">No agent reads it yet. Add it from an agent's Settings.</p>;
  return (
    <div className="flex flex-wrap gap-1">
      {users.map((a) => (
        <span key={a.id} className="max-w-full inline-flex items-center gap-1.5 h-6 pl-1 pr-2 rounded-full bg-[var(--surface)] ring-1 ring-[var(--hairline)] text-[12px] text-[var(--text-secondary)]">
          <PixelAvatar seed={a.seed} hue={a.hue} size={16} />
          <span className="truncate">{a.name}</span>
        </span>
      ))}
    </div>
  );
}

export function KbSidebar({ kbId, folderPath, place, onOpen, onAdd, onDrop, busy }: {
  kbId: string;
  /** Root-first folders down to the open one in the open base; empty at its top. */
  folderPath: KbNode[];
  /** The open folder's name, or the base's: where New adds. */
  place: string;
  onOpen: (kbId: string, folderId: string | null) => void;
  onAdd: (kind: AddKind) => void;
  onDrop: (uploads: KbUpload[], kbId: string, folderId: string | null) => void;
  /** Files are on their way into this base's folder (null = its top). */
  busy: (kbId: string, folderId: string | null) => boolean;
}) {
  const { data: bases = [] } = useKnowledgeBases();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([kbId]));
  const [dialog, setDialog] = useState<BaseDialog | null>(null);
  const folderId = folderPath[folderPath.length - 1]?.id ?? null;
  const selectedKey = folderId ?? kbId;

  // The rail shows the way to what is open, once per place opened: a chevron the person
  // closes stays closed until they open somewhere else.
  const openPath = [kbId, ...folderPath.map((f) => f.id)].join("/");
  useEffect(() => {
    const keys = openPath.split("/");
    setExpanded((prev) => (keys.every((k) => prev.has(k)) ? prev : new Set([...prev, ...keys])));
  }, [openPath]);

  const opened = bases.filter((b) => expanded.has(b.id));
  const trees = useQueries({
    queries: opened.map((b) => ({ queryKey: knowledgeKeys.nodes(b.id), queryFn: () => listKbNodes(b.id) })),
    combine: toTrees,
  });
  const treeOf = new Map(opened.map((b, i) => [b.id, trees[i]]));

  const visible: Item[] = [];
  for (const base of bases) {
    const tree = treeOf.get(base.id);
    // until a closed base is opened its folders are unknown: offer the chevron
    visible.push({ key: base.id, base, node: null, level: 1, parent: null, branches: tree ? foldersIn(tree, null).length > 0 : true });
    if (!tree) continue;
    const walk = (parentId: string | null, level: number, parentKey: string) => {
      for (const node of foldersIn(tree, parentId)) {
        const branches = foldersIn(tree, node.id).length > 0;
        visible.push({ key: node.id, base, node, level, parent: parentKey, branches });
        if (expanded.has(node.id)) walk(node.id, level + 1, node.id);
      }
    };
    walk(null, 2, base.id);
  }

  const [focusKey, setFocusKey] = useState(selectedKey);
  const rows = useRef(new Map<string, HTMLDivElement>());
  const moved = useRef(false); // focus follows the keys, not every re-render
  useEffect(() => setFocusKey(selectedKey), [selectedKey]);
  useEffect(() => {
    if (moved.current) rows.current.get(focusKey)?.focus();
    moved.current = false;
  }, [focusKey]);
  const focusable = visible.some((i) => i.key === focusKey) ? focusKey : visible[0]?.key;

  const toggle = (key: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (!next.delete(key)) next.add(key);
    return next;
  });
  const select = (item: Item) => {
    onOpen(item.base.id, item.node?.id ?? null);
    if (!expanded.has(item.key)) toggle(item.key);
  };
  const move = (key: string | null | undefined) => {
    if (!key) return;
    moved.current = true;
    setFocusKey(key);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    // a base's ⋮ and its menu sit inside the tree: their keys are theirs
    if ((e.target as HTMLElement).getAttribute("role") !== "treeitem") return;
    const at = visible.findIndex((i) => i.key === focusable);
    const item = visible[at];
    if (!item) return;
    const isOpen = expanded.has(item.key);
    const handled = {
      ArrowDown: () => move(visible[at + 1]?.key),
      ArrowUp: () => move(visible[at - 1]?.key),
      Home: () => move(visible[0].key),
      End: () => move(visible[visible.length - 1].key),
      ArrowRight: () => (item.branches && !isOpen ? toggle(item.key) : move(visible[at + 1]?.level > item.level ? visible[at + 1].key : undefined)),
      ArrowLeft: () => (item.branches && isOpen ? toggle(item.key) : move(item.parent)),
      Enter: () => select(item),
      " ": () => select(item),
    }[e.key];
    if (!handled) return;
    e.preventDefault();
    handled();
  };
  const base = bases.find((b) => b.id === kbId);

  return (
    <aside aria-label="Knowledge bases" className="w-[232px] lg:w-[264px] shrink-0 min-h-0 flex flex-col bg-[var(--surface-sunk)] border-r hairline">
      <div className="h-12 shrink-0 flex items-center px-3">
        <KbAddMenu place={place} onAdd={onAdd} className="w-full" />
      </div>
      <div className="shrink-0 flex items-center gap-1 pl-3 pr-2 pb-1">
        <span className="flex-1 section-label">Knowledge bases</span>
        <IconButton title="New knowledge base" aria-label="New knowledge base" onClick={() => setDialog({ kind: "create" })}>
          <Plus className="w-[14px] h-[14px]" strokeWidth={1.75} />
        </IconButton>
      </div>
      <div role="tree" aria-label="Knowledge bases and folders" onKeyDown={onKeyDown} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-3 space-y-[1px]">
        {visible.map((item) => (
          <div key={item.key} role="none" className="group relative">
            <RailRow
              item={item}
              open={expanded.has(item.key)}
              busy={busy(item.base.id, item.node?.id ?? null)}
              selected={item.key === selectedKey}
              focused={item.key === focusable}
              count={item.node ? treeOf.get(item.base.id)?.totals(item.node.id).files : item.base.file_count}
              onSelect={() => select(item)}
              onToggle={() => toggle(item.key)}
              onFocus={() => setFocusKey(item.key)}
              onDrop={(uploads) => onDrop(uploads, item.base.id, item.node?.id ?? null)}
              rowRef={(el) => (el ? rows.current.set(item.key, el) : rows.current.delete(item.key))}
            />
            {!item.node && (
              <BaseMenu base={item.base} onEdit={() => setDialog({ kind: "edit", base: item.base })} onDelete={() => setDialog({ kind: "delete", base: item.base })} />
            )}
          </div>
        ))}
      </div>
      {base && (
        <div className="shrink-0 border-t hairline px-4 pt-3 pb-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[
              ["files", base.file_count.toLocaleString("en-US")],
              ["storage", formatSize(base.bytes)],
              ["tokens", compact(base.tokens)],
            ].map(([label, value]) => (
              <div key={label} className="min-w-0">
                <div className="text-[13px] font-medium font-mono tabular-nums text-[var(--text-primary)] truncate">{value}</div>
                <div className="text-[11.5px] text-[var(--text-tertiary)]">{label}</div>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <div className="section-label">Used by</div>
            <UsedBy kbId={kbId} />
          </div>
        </div>
      )}
      {dialog?.kind === "create" && <KbFormDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === "edit" && <KbFormDialog base={dialog.base} onClose={() => setDialog(null)} />}
      {dialog?.kind === "delete" && <DeleteKbDialog base={dialog.base} onClose={() => setDialog(null)} />}
    </aside>
  );
}
